import { useCallback, useMemo, useState } from 'react';
import { usePurchaseOrderFollowUpMutation } from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { IPreCreateCheckModal, PreCheckMessageType } from './index';

const PRE_CHECK_MESSAGE_MAPPING: Record<PreCheckMessageType, any> = {
  MESSAGE_1: {
    title: 'Other Items are Ready to Order',
    description:
      'Other items of the same Brand are also Ready to Order or pending Price Confirmation.You can cancel and select them as well, or proceed with creating this order.',
  },
  MESSAGE_2: {
    title: 'Pending Items from Same Brand',
    description:
      'Some items of the same Brand are still pending client payment/confirmation. Follow up with your Sales Manager to check status with the client, or proceed with creating this order.',
  },
  MESSAGE_3: {
    title: 'Mixed Status Items from Same Brand',
    description:
      'Some items of the same Brand are still pending client payment/confirmation, while others are already Ready to Order or pending Price Confirmation.',
  },
  FOLLOW_UP_FAILED: {
    title: 'Follow-up failed for some orders',
    description: 'The following client orders don’t have a Sales Manager or Assistant assigned.',
  },
};

export const usePreCreateCheckModal = (props: IPreCreateCheckModal) => {
  const { onModalStateChange, preCheckMessage, modalState, onContinue, purchaseOrderItemIds } = props;
  const [isFollowUpFailed, setIsFollowUpFailed] = useState<boolean>(false);
  const [clientOrders, setClientOrders] = useState<Record<string, any>[] | undefined>(undefined);

  const { handleSuccess } = useNotificationMessages();

  const [followUp, { isLoading: followUpIsLoading }] = usePurchaseOrderFollowUpMutation();
  const onCancel = useCallback(() => {
    setIsFollowUpFailed(false);
    setClientOrders(undefined);
    onModalStateChange();
  }, [onModalStateChange]);

  const message = useMemo(() => {
    if (modalState) {
      if (isFollowUpFailed) {
        return PRE_CHECK_MESSAGE_MAPPING.FOLLOW_UP_FAILED;
      } else if (preCheckMessage) {
        return PRE_CHECK_MESSAGE_MAPPING[preCheckMessage];
      }
    } else {
      return {};
    }
  }, [modalState, preCheckMessage, isFollowUpFailed]);

  const handleContinue = useCallback(() => {
    onModalStateChange();
    onContinue();
  }, [onModalStateChange, onContinue]);

  const onFollowUp = useCallback(async () => {
    const { data = {} } = await followUp(purchaseOrderItemIds);
    const { followUpMessage, clientOrders } = data;
    if (followUpMessage === 'OK') {
      handleSuccess('The task was successfully created on Supplier Offer.');
      onCancel();
    } else if (followUpMessage === 'FOLLOW_UP_FAILED') {
      setIsFollowUpFailed(true);
      setClientOrders(clientOrders);
    }
  }, [purchaseOrderItemIds, followUp, onCancel, handleSuccess]);

  return {
    onCancel,
    PRE_CHECK_MESSAGE_MAPPING,
    message,
    handleContinue,
    onFollowUp,
    clientOrders,
    isFollowUpFailed,
    followUpIsLoading,
  };
};
